<template>
  <div>
    <div class="hero mb-5 d-flex">
      <div class="content container m-auto text-center">
        <h1>Web & Email Hosting</h1>
        <h5>
          Reliable, affordable hosting solutions for your business in the
          Philippines.
        </h5>
      </div>
    </div>

    <div class="container my-5">
      <div class="text-center mb-5">
        <h2>Choose Your Plan</h2>
        <p class="sub-heading">
          All plans include 24/7 support and 99.9% uptime guarantee
        </p>
      </div>
      <div class="row justify-content-center">
        <div
          class="col-lg-4 col-md-6 col-12 mb-4"
          v-for="(plan, key) in plans"
          :key="key"
        >
          <div
            class="card hosting-card"
            :class="{ 'hosting-card--featured': plan.featured }"
          >
            <div class="ribbon ribbon-top-right" v-if="plan.popular">
              <span>POPULAR</span>
            </div>
            <div class="card-body text-center">
              <div class="hosting-icon">
                <i :class="plan.icon"></i>
              </div>
              <h4 class="hosting-title">{{ plan.title }}</h4>
              <p class="hosting-subtitle">{{ plan.subtitle }}</p>
              <div class="hosting-price">
                <span class="currency">PHP</span>
                <span class="amount">{{ plan.price }}</span>
                <span class="period">/mo</span>
              </div>
            </div>
            <ul class="list-group list-group-flush">
              <li
                class="list-group-item"
                v-for="(feature, i) in plan.features"
                :key="i"
              >
                <i class="fas fa-check feature-check"></i>
                {{ feature }}
              </li>
            </ul>
            <div class="card-body text-center mt-auto">
              <a
                href="/#contact"
                class="btn btn-lg hosting-btn"
                :class="
                  plan.featured ? 'btn-secondary' : 'btn-outline-light'
                "
              >
                Get Started
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="container my-5 py-5">
      <div class="text-center mb-5">
        <h2>Why Choose Our Hosting?</h2>
      </div>
      <div class="row text-center">
        <div
          class="col-lg-3 col-md-6 col-12 mb-4"
          v-for="(item, i) in highlights"
          :key="i"
        >
          <div class="highlight-card">
            <i :class="item.icon" class="highlight-icon"></i>
            <h5>{{ item.title }}</h5>
            <p>{{ item.description }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  title: "Trident Network - Hosting",
  data() {
    return {
      plans: [
        {
          title: "Mail Hosting",
          subtitle: "Starter",
          icon: "fas fa-envelope",
          price: "199",
          popular: false,
          featured: false,
          features: [
            "1 mailbox included",
            "5GB for emails",
            "2GB for files",
            "Full mobile sync support",
            "Anti-spam protection"
          ]
        },
        {
          title: "Shared Hosting",
          subtitle: "Starter",
          icon: "fas fa-server",
          price: "399",
          popular: true,
          featured: true,
          features: [
            "Domain Name included",
            "3 Websites",
            "Free cPanel access",
            "20 GB SSD storage",
            "Managed Hosting"
          ]
        },
        {
          title: "WordPress Hosting",
          subtitle: "Starter",
          icon: "fab fa-wordpress",
          price: "499",
          popular: false,
          featured: false,
          features: [
            "10 GB SSD storage",
            "100k visitors/month",
            "Free SSL certificate",
            "Managed Hosting",
            "99.99% uptime guarantee"
          ]
        }
      ],
      highlights: [
        {
          icon: "fas fa-shield-alt",
          title: "Secure",
          description:
            "Free SSL certificates and anti-spam protection on all plans."
        },
        {
          icon: "fas fa-tachometer-alt",
          title: "Fast",
          description:
            "SSD-powered servers for lightning-fast load times."
        },
        {
          icon: "fas fa-headset",
          title: "24/7 Support",
          description:
            "Our team is always available to help you with any issue."
        },
        {
          icon: "fas fa-sync-alt",
          title: "99.9% Uptime",
          description:
            "We guarantee your website stays online around the clock."
        }
      ]
    };
  },
  mounted() {
    window.scrollTo(0, 0);
  }
};
</script>

<style lang="scss" scoped>
.sub-heading {
  color: #b0b0b0;
  font-size: 18px;
}

.hosting-card {
  background: #131516;
  border: 1px solid #242424;
  border-radius: 12px;
  padding: 30px 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, border-color 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-6px);
    border-color: #333;
  }

  &--featured {
    border-color: #00ffd0;
    background: linear-gradient(180deg, #131516 0%, #0f1a1d 100%);

    &:hover {
      border-color: #00ffd0;
    }
  }

  .list-group {
    border-color: #2d2d2d;
  }

  .list-group-item {
    background: transparent;
    border-color: #2d2d2d;
    color: #e0e0e0;
    font-size: 15px;
    padding: 10px 16px;
  }

  .feature-check {
    color: #00ffd0;
    margin-right: 8px;
    font-size: 13px;
  }
}

.hosting-icon {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: #1c2225;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;

  i {
    font-size: 28px;
    color: #00ffd0;
  }

  .hosting-card--featured & {
    background: linear-gradient(135deg, #00ffd0, #1b86b9);

    i {
      color: #000;
    }
  }
}

.hosting-title {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 2px;
}

.hosting-subtitle {
  color: #999;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 20px;
}

.hosting-price {
  margin-bottom: 10px;

  .currency {
    font-size: 16px;
    color: #b0b0b0;
    vertical-align: super;
    margin-right: 2px;
  }

  .amount {
    font-size: 48px;
    font-weight: 700;
    color: #fff;
    line-height: 1;
  }

  .period {
    font-size: 16px;
    color: #b0b0b0;
  }
}

.hosting-btn {
  width: 100%;
  padding: 12px;
  font-weight: 600;
  font-size: 16px;
  border-radius: 8px;

  &.btn-outline-light {
    border-color: #333;
    color: #e0e0e0;

    &:hover {
      background: #1c2225;
      border-color: #00ffd0;
      color: #fff;
    }
  }
}

.highlight-card {
  padding: 30px 20px;
  border-radius: 12px;
  background: #131516;
  border: 1px solid #242424;
  height: 100%;
  transition: border-color 0.3s ease;

  &:hover {
    border-color: #333;
  }

  p {
    color: #b0b0b0;
    font-size: 15px;
    margin-bottom: 0;
  }
}

.highlight-icon {
  font-size: 32px;
  color: #00ffd0;
  margin-bottom: 16px;
}
</style>
